/**
 * S Contract list — mirror Access subform `frmMatchingPSMatchLocalSQry`.
 *
 * Migrated 2026-05-26 to skill `/table-style` (FilterBar + per-column ▽) +
 * `/user-logaction` (BE registry `matching-ps/columns` sub-key 's-contract').
 *
 * Access spec:
 *  - 22 cột ColumnOrder 0..21 + 2 cột không ColumnOrder (`delivery_from2`,
 *    `delivery_to2` — Caption "Delivery From"/"Delivery To") xếp cuối list.
 *  - Cột 20 `is_finalized` ControlSource=`finalized_txt` → web đọc `finalizedTxt`.
 *  - Cột 0 `is_checked` ConditionalFormat: value `"Check"` → bg đỏ `#ED1C24`,
 *    text trắng (xác thực từ hex `ed1c24 00 ffffff` trong file form Access).
 *  - Sort: query `MatchingPSMatch_Ref_LocalS` `OrderByOn=0` → BE chỉ giữ
 *    `contract_sale ASC` làm tiebreaker; FE mặc định `sortState=null`.
 *  - Access form **không** set `TotalsRow`; webapp render footer Total tự thêm
 *    theo yêu cầu user — sum chạy trên `filteredRows`.
 *
 * Data source: `useGetMatchingPsSContractQuery` → BE `/matching-ps/s-contract-rows`
 * → `public.v_report_matching_locals`. Fetch all rows một lần (ALL_ROWS_PAGE_SIZE),
 * filter/sort/paginate client-side.
 */
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'
import { type SxProps, type Theme } from '@mui/material/styles'
import { useMemo, useState, type CSSProperties, type MouseEvent } from 'react'
import { TableVirtuoso } from 'react-virtuoso'

import BrandState from '../../../components/BrandState'
import {
  type ColumnFilterAnchorPosition,
  ColumnFilterIcon,
  ColumnFilterPopover,
  type ColumnFilterKind,
  FilterBar,
  FilterButton,
  FilterPopover,
  SearchFilterContent,
  distinctColumnValues,
  filterBarBtnSx,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  rowMatchesSearch,
  useColumnFilters,
  useFilterPopover,
  useRowSelection,
} from '../../../components/dataList'
import {
  HIDE_ON_MOBILE_INLINE,
  HIDE_ON_TABLET_INLINE,
  TABLE_PAGINATION_COMPACT,
} from '../../../theme/responsive'
import ResizeHandle from '../../../components/ResizeHandle'
import { useColumnWidths } from '../../../hooks/useColumnWidths'
import { useGetMatchingPsSContractQuery } from '../../../store/api/matchingPsApi'
import {
  getTableSortSuffix,
  sortTableRows,
  type TableSortState,
  toggleTableSort,
} from '../../../utils/tableSort'
import { usePreferenceColumns } from '../../../utils/datasheetColumns/usePreferenceColumns'
import { FuturesColumnsMenu } from '../../futures/FuturesColumnsMenu'

/** TablePagination «All» maps to this pageSize. */
const ALL_ROWS_PAGE_SIZE = 1_000_000
const WIDTHS_TABLE_KEY = 'matching.s-contract.list'

type ColumnKind = 'text' | 'date' | 'number'

interface ColumnDef {
  key: string
  label: string
  kind?: ColumnKind
  align?: 'right'
  filter?: ColumnFilterKind
}

/** ColumnOrder Access 0..21 — match form_frmMatchingPSMatchLocalSQry.txt. */
const TABLE_COLUMNS: ColumnDef[] = [
  { key: 'isChecked', label: 'Check ?', filter: 'checkboxes' },
  { key: 'contractSale', label: 'Contract Sale' },
  { key: 'buyersRef', label: "Buyer's Ref" },
  { key: 'contractDate', label: 'Contract Date', kind: 'date' },
  { key: 'sDirectMatched', label: 'S - Direct Matched', kind: 'number', align: 'right' },
  { key: 'sInstructed', label: 'S - Instructed', kind: 'number', align: 'right' },
  { key: 'sUnmatchUninstructed', label: 'S - Unmatch & Uninstructed', kind: 'number', align: 'right' },
  { key: 'matchInfo', label: 'S - Instructed Info' },
  { key: 'doInfo', label: 'S - DO Info' },
  { key: 'cpCode', label: 'CP Code', filter: 'checkboxes' },
  { key: 'shortName', label: 'Short Name', filter: 'checkboxes' },
  { key: 'quantity', label: 'Quantity', kind: 'number', align: 'right' },
  { key: 'packaging', label: 'Package ENG', filter: 'checkboxes' },
  { key: 'quality', label: 'Quality', filter: 'checkboxes' },
  { key: 'certification', label: 'Certification', filter: 'checkboxes' },
  { key: 'contractType', label: 'Contract Type', filter: 'checkboxes' },
  { key: 'deliveryTerms', label: 'Delivery Terms', filter: 'checkboxes' },
  { key: 'warehouse', label: 'Warehouse', filter: 'checkboxes' },
  { key: 'deliveryFrom', label: 'Shipment From', kind: 'date' },
  { key: 'deliveryTo', label: 'Shipment To', kind: 'date' },
  { key: 'finalizedTxt', label: 'Is Finalized?', filter: 'checkboxes' },
  { key: 'delivered', label: 'Delivered', kind: 'number', align: 'right' },
  { key: 'deliveryFrom2', label: 'Delivery From', kind: 'date' },
  { key: 'deliveryTo2', label: 'Delivery To', kind: 'date' },
]

/** Cột numeric tổng ở footer Total — bám 5 ControlSource numeric trong Access. */
const TOTAL_COLUMNS: ReadonlySet<string> = new Set([
  'sDirectMatched',
  'sInstructed',
  'sUnmatchUninstructed',
  'quantity',
  'delivered',
])

/** Cột freeze khi scroll ngang — chỉ Contract Sale, Check ? vẫn scroll bình thường. */
const FROZEN_KEYS: ReadonlySet<string> = new Set(['contractSale'])
const FROZEN_FALLBACK_WIDTH: Record<string, number> = {
  contractSale: 150,
}

const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const formatAccessDate = (value: unknown): string => {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  const month = ACCESS_MONTHS[d.getMonth()]
  const year = `${d.getFullYear()}`
  return `${day}-${month}-${year}`
}

const formatAccessNumber = (value: number): string => {
  if (Number.isInteger(value)) return value.toLocaleString('en-US')
  return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

type Row = Record<string, unknown> & { __rid?: number }

const cellValue = (row: Row, col: ColumnDef): string => {
  const val = row[col.key]
  if (val == null || val === '') return ''
  if (col.kind === 'date') return formatAccessDate(val)
  if (col.kind === 'number' && typeof val === 'number') return formatAccessNumber(val)
  if (typeof val === 'boolean') return val ? 'Yes' : 'No'
  if (typeof val === 'number') return formatAccessNumber(val)
  return String(val)
}

/** Access ConditionalFormat: cell value `"Check"` → bg đỏ + text trắng. */
const CHECK_HIGHLIGHT_SX: SxProps<Theme> = {
  bgcolor: '#ED1C24',
  color: '#FFFFFF',
  fontWeight: 600,
  '& .app-table-cell-text': { color: '#FFFFFF' },
}

export default function SContractTab() {
  const {
    selectedId: selectedRid,
    toggleSelect: toggleSelectRid,
  } = useRowSelection<number>()
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(-1)
  const { getWidth, startResize } = useColumnWidths(WIDTHS_TABLE_KEY)

  const [searchTerm, setSearchTerm] = useState('')
  const [sortState, setSortState] = useState<TableSortState<string> | null>(null)

  const filterPopover = useFilterPopover<'search'>()
  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{
    key: string
    position: ColumnFilterAnchorPosition
  } | null>(null)
  const closeColPopover = () => setColPopover(null)

  const columnsCtrl = usePreferenceColumns<ColumnDef>({
    scope: 'matching-ps',
    prefKey: 'columns',
    subKey: 's-contract',
    allColumns: TABLE_COLUMNS,
    widthsTableKey: WIDTHS_TABLE_KEY,
  })
  const { visibleColumns, openMenu: openColMenu } = columnsCtrl

  const { data, isLoading, isFetching, error } = useGetMatchingPsSContractQuery(
    { page: 1, pageSize: ALL_ROWS_PAGE_SIZE },
    { refetchOnMountOrArgChange: true },
  )

  const baseRows = useMemo<Row[]>(
    () => (data?.items ?? []).map((r, idx) => ({ ...(r as Row), __rid: idx })),
    [data],
  )

  const hasActiveFilters = !!searchTerm.trim() || columnFilters.hasAny
  const activeFilterCount = (searchTerm.trim() ? 1 : 0) + columnFilters.activeKeys.length
  const clearAllFilters = () => {
    setSearchTerm('')
    columnFilters.clearAll()
    setPage(0)
    filterPopover.close()
  }

  const filteredRows = useMemo(
    () =>
      baseRows.filter(
        (row) =>
          rowMatchesSearch(row, searchTerm, TABLE_COLUMNS) &&
          rowMatchesAllColumnFilters(row, columnFilters.filters),
      ),
    [baseRows, columnFilters.filters, searchTerm],
  )

  const sortedRows = useMemo(
    () => sortTableRows(filteredRows, sortState, TABLE_COLUMNS),
    [filteredRows, sortState],
  )
  const pageRows = useMemo(() => {
    if (rowsPerPage === -1) return sortedRows
    const start = page * rowsPerPage
    return sortedRows.slice(start, start + rowsPerPage)
  }, [page, rowsPerPage, sortedRows])

  /** Sum 5 cột numeric trên filteredRows — render thành Total footer row. */
  const totals = useMemo<Record<string, number>>(() => {
    const acc: Record<string, number> = {}
    for (const key of TOTAL_COLUMNS) acc[key] = 0
    for (const r of filteredRows) {
      for (const key of TOTAL_COLUMNS) {
        const v = r[key]
        if (typeof v === 'number' && Number.isFinite(v)) acc[key] += v
      }
    }
    return acc
  }, [filteredRows])

  const popoverColumn = useMemo<ColumnDef | null>(() => {
    if (!colPopover) return null
    return TABLE_COLUMNS.find((c) => c.key === colPopover.key) ?? null
  }, [colPopover])
  const popoverKind = useMemo<ColumnFilterKind>(
    () =>
      popoverColumn ? inferColumnFilterKind(popoverColumn as never, baseRows) : 'text',
    [popoverColumn, baseRows],
  )
  const popoverOptions = useMemo(
    () =>
      popoverKind === 'checkboxes' && colPopover
        ? distinctColumnValues(baseRows, String(colPopover.key))
        : undefined,
    [popoverKind, colPopover, baseRows],
  )

  /** Hiển thị "Contract Sale: first–last" của trang hiện tại (parity idRangeText). */
  const contractRangeText = useMemo(() => {
    if (pageRows.length === 0) return 'Contract: —'
    const first = String(pageRows[0].contractSale ?? '').trim() || '—'
    const last = String(pageRows[pageRows.length - 1].contractSale ?? '').trim() || '—'
    return first === last ? `Contract: ${first}` : `Contract: ${first} … ${last}`
  }, [pageRows])

  const minTableWidth = Math.max(960, visibleColumns.length * 110)
  const gridKey = `${rowsPerPage}:${page}:${filteredRows.length}:${visibleColumns.length}`

  const stickyOffsets = useMemo(() => {
    const offsets: Record<string, number> = {}
    let acc = 0
    for (const col of visibleColumns) {
      if (!FROZEN_KEYS.has(col.key)) continue
      offsets[col.key] = acc
      const w = getWidth(col.key) ?? FROZEN_FALLBACK_WIDTH[col.key] ?? 120
      acc += w
    }
    return offsets
  }, [visibleColumns, getWidth])

  const toggleSort = (key: string) => {
    setSortState((cur) => toggleTableSort(cur, key))
    setPage(0)
  }

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', p: 1 }}>
      <FilterBar
        hasActiveFilters={hasActiveFilters}
        onClearAll={clearAllFilters}
        activeFilterCount={activeFilterCount}
      >
        <FilterButton
          label="Search"
          active={!!searchTerm.trim()}
          onClick={(e: MouseEvent<HTMLElement>) => filterPopover.open('search', e)}
        />
        <Button
          size="small"
          variant="outlined"
          endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
          onClick={(e) => openColMenu(e.currentTarget)}
          sx={filterBarBtnSx}
          title="Columns"
        >
          <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Columns</Box>
          <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>Cols</Box>
        </Button>
      </FilterBar>

      <Divider sx={{ mb: 0.5 }} />

      {error ? (
        <Alert severity="error" sx={{ mb: 1 }}>
          Failed to load S Contract overview.
        </Alert>
      ) : null}

      <FilterPopover
        open={filterPopover.state !== null}
        anchorEl={filterPopover.state?.anchor ?? null}
        onClose={() => filterPopover.close()}
        maxWidth={320}
      >
        {filterPopover.isOpen('search') && (
          <SearchFilterContent
            value={searchTerm}
            onChange={(v) => {
              setSearchTerm(v)
              setPage(0)
            }}
            placeholder="Contract sale, CP code, quality, ref…"
            label="Search contracts"
            onClear={() => filterPopover.close()}
          />
        )}
      </FilterPopover>

      <ColumnFilterPopover
        open={colPopover !== null}
        anchorPosition={colPopover?.position ?? null}
        onClose={closeColPopover}
        label={popoverColumn?.label ?? ''}
        kind={popoverKind === 'none' ? 'text' : popoverKind}
        value={colPopover ? columnFilters.filters[String(colPopover.key)] : undefined}
        onChange={(v) => {
          if (!colPopover) return
          columnFilters.setFilter(String(colPopover.key), v)
          setPage(0)
        }}
        options={popoverOptions}
      />

      <Box
        className="app-dataframe"
        sx={{
          flex: 1,
          minHeight: 0,
          // Override default CSS clamp (calc(100dvh - 260px)) — Matching page chỉ
          // có Tabs (~38px) + filter bar phía trên, overhead nhỏ hơn nhiều. Để
          // flex:1 lấp đầy CardContent thay vì để khoảng trống dưới bảng.
          height: 'auto',
        }}
      >
        <Box
          className="app-dataframe-toolbar"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 0.5,
            flexWrap: 'wrap',
            minHeight: 24,
            py: 0,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={0.4} sx={{ flexShrink: 0 }}>
            {(isLoading || isFetching) && <CircularProgress size={15} />}
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>
              Total: {filteredRows.length}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>
              Loaded: {baseRows.length}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>
              {contractRangeText}
            </Typography>
            <TablePagination
              component="div"
              count={filteredRows.length}
              page={rowsPerPage === -1 ? 0 : page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(Number(e.target.value))
                setPage(0)
              }}
              rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]}
              labelRowsPerPage="Rows"
              sx={TABLE_PAGINATION_COMPACT}
            />
          </Stack>
        </Box>

        <Box sx={{ flex: 1, minHeight: 320, overflow: 'hidden' }}>
          {error ? (
            <BrandState
              tone="error"
              title="Could not load S Contract overview"
              description="Please refresh the page or contact the system administrator if the issue continues."
            />
          ) : isLoading && baseRows.length === 0 ? (
            <BrandState tone="loading" title="Loading…" description="Please wait." />
          ) : pageRows.length === 0 ? (
            <BrandState
              title="No contracts found"
              description="Try adjusting the filters above."
            />
          ) : (
            <TableVirtuoso
              key={gridKey}
              style={{ height: '100%' }}
              data={pageRows}
              fixedHeaderContent={() => (
                <TableRow>
                  {visibleColumns.map((col) => {
                    const w = getWidth(col.key)
                    const isFrozen = FROZEN_KEYS.has(col.key)
                    const headerStyle: CSSProperties = {}
                    if (w) {
                      headerStyle.width = w
                      headerStyle.minWidth = w
                      headerStyle.maxWidth = w
                    }
                    if (isFrozen) {
                      headerStyle.position = 'sticky'
                      headerStyle.left = stickyOffsets[col.key] ?? 0
                      headerStyle.zIndex = 7
                    }
                    return (
                      <TableCell
                        key={col.key}
                        align={col.align}
                        className="app-table-header-cell"
                        onClick={() => toggleSort(col.key)}
                        style={Object.keys(headerStyle).length ? headerStyle : undefined}
                        sx={{
                          whiteSpace: 'nowrap',
                          cursor: 'pointer',
                          userSelect: 'none',
                          ...(isFrozen ? {} : { position: 'relative' }),
                        }}
                      >
                        {col.label}
                        {getTableSortSuffix(sortState, col.key)}
                        <ColumnFilterIcon
                          active={columnFilters.isActive(col.key)}
                          onClick={(position) => setColPopover({ key: col.key, position })}
                          ariaLabel={`Filter ${col.label}`}
                        />
                        <ResizeHandle onMouseDown={(e) => startResize(col.key, e)} />
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              fixedFooterContent={() => (
                <TableRow
                  sx={{
                    bgcolor: 'action.hover',
                    '& td': {
                      fontWeight: 700,
                      borderTop: '1px solid',
                      borderTopColor: 'divider',
                    },
                  }}
                >
                  {visibleColumns.map((col, idx) => {
                    const w = getWidth(col.key)
                    const isTotalCol = TOTAL_COLUMNS.has(col.key)
                    const isFrozen = FROZEN_KEYS.has(col.key)
                    const label = idx === 0 ? 'Total' : isTotalCol ? formatAccessNumber(totals[col.key] ?? 0) : ''
                    return (
                      <TableCell
                        key={col.key}
                        align={col.align}
                        style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                        sx={{
                          whiteSpace: 'nowrap',
                          py: 0.5,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          ...(isFrozen && {
                            position: 'sticky',
                            left: stickyOffsets[col.key] ?? 0,
                            zIndex: 4,
                            backgroundColor: (t) =>
                              t.palette.mode === 'dark' ? '#243042' : '#eef1f5',
                          }),
                        }}
                      >
                        <Box
                          component="span"
                          className="app-table-cell-text"
                          sx={{ display: 'inline-block', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}
                        >
                          {label}
                        </Box>
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              itemContent={(_: number, row: Row) =>
                visibleColumns.map((col) => {
                  const val = cellValue(row, col)
                  const w = getWidth(col.key)
                  const isCheckCell = col.key === 'isChecked' && row.isChecked === 'Check'
                  const isFrozen = FROZEN_KEYS.has(col.key)
                  return (
                    <TableCell
                      key={col.key}
                      align={col.align}
                      style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                      sx={{
                        whiteSpace: 'nowrap',
                        py: 0.5,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        ...(isFrozen && {
                          position: 'sticky',
                          left: stickyOffsets[col.key] ?? 0,
                          zIndex: 1,
                          backgroundColor: 'background.paper',
                        }),
                        ...(isCheckCell ? (CHECK_HIGHLIGHT_SX as Record<string, unknown>) : {}),
                      }}
                    >
                      <Box
                        component="span"
                        className="app-table-cell-text"
                        title={val || '-'}
                        sx={{ display: 'inline-block', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}
                      >
                        {val || '-'}
                      </Box>
                    </TableCell>
                  )
                })
              }
              components={{
                /* eslint-disable @typescript-eslint/no-explicit-any */
                Table: (props: any) => (
                  <Table {...props} size="small" stickyHeader sx={{ minWidth: minTableWidth, width: '100%' }} />
                ),
                TableHead: ((props: any) => <TableHead {...props} component="thead" />) as any,
                TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as any,
                TableFoot: ((props: any) => (
                  <TableFooter
                    {...props}
                    component="tfoot"
                    sx={{
                      position: 'sticky',
                      bottom: 0,
                      bgcolor: 'background.paper',
                      zIndex: 2,
                    }}
                  />
                )) as any,
                TableRow: (props: any) => {
                  const idx = Number(props['data-item-index'])
                  const rid = Number.isFinite(idx) ? (pageRows[idx]?.__rid ?? null) : null
                  return (
                    <TableRow
                      {...props}
                      hover
                      selected={rid != null && rid === selectedRid}
                      sx={{ cursor: 'pointer' }}
                      onClick={() => {
                        if (rid != null) toggleSelectRid(rid)
                      }}
                    />
                  )
                },
                /* eslint-enable @typescript-eslint/no-explicit-any */
              }}
            />
          )}
        </Box>
      </Box>

      <FuturesColumnsMenu controller={columnsCtrl} />
    </Box>
  )
}
